import _ from 'lodash';
import { type JSX, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { TprmQuestionnaireApi } from 'Api/TPRM/TprmQuestionnaireApi';
import { Button } from 'Components/Buttons/Buttons';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { SidebarSection as PageSidebarSection } from 'Components/PageLayout/PageLayoutQuestionnaire/PageLayoutQuestionnaire';
import { RequiresAttentionMarker } from 'Components/RequiresAttentionMarker/RequiresAttentionMarker';
import { Text } from 'Components/Text/Text';
import { DiscussionThread } from 'Components/UcrmTprm/DiscussionThread/DiscussionThread';
import { QuestionnaireForm } from 'Components/UcrmTprm/QuestionnaireForm/QuestionnaireForm';
import { TPRM_PUBLIC_QUESTIONNAIRE, TPRM_QUESTIONNAIRE_LIST } from 'Config/PathTemplates';
import { BasicAuthHandler } from 'Helpers/Auth/BasicAuth/BasicAuthHandler';
import { iso8601ToUsDateShort, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { DueDiligenceAnswer, SaveQuestionnaireControlGroupRequest } from 'Models/DueDiligence';
import { PublicQuestionnaire } from 'Models/TPRM';

export interface ThirdPartyQuestionnaireLoaderProps {
    authHandler: BasicAuthHandler;
    documentApi: DocumentApi;
    questionnaireApi: TprmQuestionnaireApi;
}

export const ThirdPartyQuestionnaireLoader = (props: ThirdPartyQuestionnaireLoaderProps) => {
    return <PageDataLoader path={TPRM_PUBLIC_QUESTIONNAIRE} fetchData={(urlParams) => Promise.all([props.questionnaireApi.getClientAndThirdPartyDetails(), props.questionnaireApi.getQuestionnaire(urlParams.third_party_id, urlParams.service_id, urlParams.assessment_id)])} renderForm={(result, params) => <ThirdPartyQuestionnaire authHandler={props.authHandler} clientId={params.client_id} clientName={result[0].data.client_name} documentApi={props.documentApi} questionnaireApi={props.questionnaireApi} initialQuestionnaire={result[1].data} thirdPartyName={result[0].data.third_party_name} />} />;
};

export interface ThirdPartyQuestionnaireProps {
    authHandler: BasicAuthHandler;
    clientId: string;
    clientName: string;
    documentApi: DocumentApi;
    initialQuestionnaire: PublicQuestionnaire;
    questionnaireApi: TprmQuestionnaireApi;
    thirdPartyName: string;
}

/**
 * Renders a questionnaire for a third-party user to complete.
 * Counterpart to `DueDiligenceAssessment`, where SummIT users assess responses to questions.
 * Answering questions is handled by `QuestionnaireForm`; this page provides the TPRM-specific data access, status, and discussion.
 */
const ThirdPartyQuestionnaire = (props: ThirdPartyQuestionnaireProps): JSX.Element => {
    const userEmailAddress = useMemo(() => props.authHandler.getAuthenticatedUserEmail()!, [props.authHandler]);

    /** Owned here rather than by `DiscussionThread` so that a partially written message survives the user switching to a different section of the questionnaire. */
    const [draftDiscussionMessage, setDraftDiscussionMessage] = useState('');

    const saveControlGroup = async (questionnaire: PublicQuestionnaire, frameworkId: string, groupId: string, answers: DueDiligenceAnswer[]): Promise<PublicQuestionnaire> => {
        const request: SaveQuestionnaireControlGroupRequest = { answers };
        return (await props.questionnaireApi.saveControlGroup(questionnaire.third_party_id, questionnaire.service_id, questionnaire.id, frameworkId, groupId, request)).data;
    };

    const submitQuestionnaire = async (questionnaire: PublicQuestionnaire): Promise<PublicQuestionnaire> => {
        return (await props.questionnaireApi.submitQuestionnaire(questionnaire.third_party_id, questionnaire.service_id, questionnaire.id)).data;
    };

    const getLeadingSidebarSections = (questionnaire: PublicQuestionnaire, onQuestionnaireChange: (updatedQuestionnaire: PublicQuestionnaire) => void): PageSidebarSection[] => {
        const lastReadTimestampForUser: string | undefined = questionnaire.discussion.marked_as_read_timestamps_third_party[userEmailAddress];
        const unreadMessageCount = lastReadTimestampForUser ? questionnaire.discussion.messages.filter((message) => message.timestamp > lastReadTimestampForUser).length : questionnaire.discussion.messages.length;

        /** Marks a discussion as read in the background. For immediate feedback, sets the "last read" timestamp in state to a time in the far future; the real timestamp will be loaded next time this page is loaded from scratch. */
        const markMessagesAsRead = () => {
            const updatedQuestionnaire = _.cloneDeep(questionnaire);
            updatedQuestionnaire.discussion.marked_as_read_timestamps_third_party[userEmailAddress] = jsDateToIso8601(new Date(9999, 0));
            onQuestionnaireChange(updatedQuestionnaire);

            props.questionnaireApi.markDiscussionAsRead(questionnaire.third_party_id, questionnaire.service_id, questionnaire.id).catch((error) => {
                console.error('Failed to mark discussion as read:', error);
            });
        };

        return [
            {
                title: 'Status and Discussion',
                content: [
                    {
                        title: 'Status',
                        subtitle: questionnaire.submitted_timestamp ? `The questionnaire was completed on ${iso8601ToUsDateShort(questionnaire.submitted_timestamp)}.` : 'The questionnaire is in progress.',
                        content: questionnaire.submitted_timestamp ? (
                            <Text>Answers cannot be updated, but discussion messages can still be sent.</Text>
                        ) : (
                            <Text>
                                {`Please respond to the questions in each section using the sidebar on the right side of the page. When the questionnaire is completed, submit it to ${props.clientName} via the `}
                                <i>Submit</i> section.
                            </Text>
                        ),
                    },
                    {
                        title: 'Discussion',
                        content: (
                            <>
                                <Text>
                                    Messages may be sent between {props.clientName} and {props.thirdPartyName}.
                                </Text>
                                {unreadMessageCount > 0 && (
                                    <>
                                        <Text variant="Text3" color="darkGray" noStyles>
                                            Messages were added since you last viewed the discussion.
                                        </Text>
                                        <Button variant="linkText" onClick={markMessagesAsRead} size="sm">
                                            Mark as Read
                                        </Button>
                                    </>
                                )}
                                <DiscussionThread perspective="questionnaire" questionnaire={questionnaire} questionnaireApi={props.questionnaireApi} discussion={questionnaire.discussion} onMessageSent={onQuestionnaireChange} clientName={props.clientName} organizationName={props.thirdPartyName} draftMessage={draftDiscussionMessage} onDraftMessageChange={setDraftDiscussionMessage} />
                            </>
                        ),
                    },
                ],
                additionalContent:
                    unreadMessageCount > 0 ? (
                        <RequiresAttentionMarker type="warning" size="sm">
                            {`${unreadMessageCount} unread message${unreadMessageCount === 1 ? '' : 's'}`}
                        </RequiresAttentionMarker>
                    ) : undefined,
            },
        ];
    };

    return (
        <QuestionnaireForm<PublicQuestionnaire>
            documentApi={props.documentApi}
            getLeadingSidebarSections={getLeadingSidebarSections}
            headerBreadcrumb={
                <Breadcrumb>
                    <BreadcrumbLink link={generatePath(TPRM_QUESTIONNAIRE_LIST, { client_id: props.clientId, third_party_id: props.initialQuestionnaire.third_party_id, service_id: props.initialQuestionnaire.service_id })}>Questionnaires</BreadcrumbLink>
                    <BreadcrumbText>{props.initialQuestionnaire.name}</BreadcrumbText>
                </Breadcrumb>
            }
            headerDescription={`${props.clientName} has requested that ${props.thirdPartyName} completes this questionnaire${props.initialQuestionnaire.due_date ? ` before ${iso8601ToUsDateShort(props.initialQuestionnaire.due_date)}.` : '.'}`}
            headerTitle={props.initialQuestionnaire.name}
            initialQuestionnaire={props.initialQuestionnaire}
            isSubmitted={(questionnaire) => !!questionnaire.submitted_timestamp}
            notifiedParty={props.clientName}
            saveControlGroup={saveControlGroup}
            submitQuestionnaire={submitQuestionnaire}
        />
    );
};
